<template>
  <BaseFormFieldLayout
    :id="id"
    :label="label"
    :note="note"
    :error-message="errorMessage"
    :field-class="fieldClass"
  >
    <div class="d-flex justify-content-between" :class="{ 'is-invalid': !!errorMessage }">
      <div class="flag-select me-2">
        <ASelect
          :value="selectedCountryCode"
          size="large"
          class="w-100"
          option-filter-prop="label"
          show-search
          :get-popup-container="getPopupContainer"
          :placeholder="t('Select country')"
          :dropdown-match-select-width="200"
          :disabled="attrs.disabled"
          @change="onCountryChange"
        >
          <ASelectOptGroup v-if="preferredCountry">
            <template #label>
              <span>
                <strong>{{ t('Choose a Country') }}</strong>
              </span>
            </template>
            <ASelectOption
              :label="preferredCountry.name"
              :value="preferredCountry.code"
              :title="preferredCountry.name"
            >
              <SanitizedText
                :text="CountryFlagSvg[preferredCountry.code]"
                as="i"
                role="img"
                class="flag d-inline-block me-2"
              />
              <span class="select-label">{{ preferredCountry.name }}</span>
            </ASelectOption>
          </ASelectOptGroup>
          <ASelectOptGroup>
            <template #label>
              <span class="group-border text-muted d-inline-block w-100"></span>
            </template>
            <ASelectOption
              v-for="(country, index) in allCountries"
              :key="`select-option-${index}-${country.code}`"
              :label="country.name"
              :value="country.code"
              :title="country.name"
            >
              <SanitizedText
                :text="CountryFlagSvg[country.code]"
                as="i"
                role="img"
                class="flag d-inline-block me-2"
              />
              <span class="select-label">{{ country.name }}</span>
            </ASelectOption>
          </ASelectOptGroup>
        </ASelect>
      </div>
      <div class="col">
        <input
          v-bind="$attrs"
          :id="id"
          :value="formattedPhoneValue"
          type="tel"
          name="phone"
          class="form-control"
          :class="{ 'is-invalid': isPhoneInvalid || !!errorMessage }"
          @input="onPhoneInput"
          @blur="reformatNumber"
        />
      </div>
    </div>
  </BaseFormFieldLayout>
</template>

<script lang="ts" setup>
import { computed, ref, useAttrs, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { CountryCode } from 'libphonenumber-js/types'
import { formatIncompletePhoneNumber } from 'libphonenumber-js/max'
import CountryList from 'country-list-with-dial-code-and-flag'
import CountryFlagSvg from 'country-list-with-dial-code-and-flag/dist/flag-svg'
import { CountryCodeIso2 } from '@/shared'
import {
  getPhoneNumberCountry,
  isValidPhoneNumber,
  toInternational,
  toNationalPretty,
} from '@/helpers/phoneNumber.helper'
import { asyncComponentWrapper } from '@/composables/useReloadModal'
import SanitizedText from '@/components/Misc/SanitizedText.vue'
import {
  SelectOptGroup as ASelectOptGroup,
  SelectOption as ASelectOption,
} from 'ant-design-vue/es/select'

const ASelect = asyncComponentWrapper(() => import('ant-design-vue/es/select'))

const allCountries = CountryList.getAll()

defineOptions({ inheritAttrs: false })

const attrs = useAttrs()
const model = defineModel<string | null>()

const props = withDefaults(
  defineProps<{
    id: string
    label?: string
    note?: string
    errorMessage?: string
    fieldClass?: string
    defaultCountryLocale?: string
  }>(),
  {
    label: '',
    note: '',
    errorMessage: '',
    fieldClass: '',
    defaultCountryLocale: CountryCodeIso2.US,
  },
)

const { t } = useI18n()

const getPopupContainer = (triggerNode: HTMLElement) => triggerNode.parentNode as HTMLElement

// first option in country dropdown
const preferredCountry = computed(() =>
  allCountries.find((country) => props.defaultCountryLocale === country.code),
)

const selectedCountryCode = ref<CountryCode>(CountryCodeIso2.US as CountryCode)
const formattedPhoneValue = ref<string>('')

const isPhoneInvalid = computed(
  () =>
    formattedPhoneValue.value.length &&
    !isValidPhoneNumber(formattedPhoneValue.value, selectedCountryCode.value),
)

// select new countryCode and update international format
const onCountryChange = (countryCode: CountryCode) => {
  selectedCountryCode.value = countryCode
  model.value = toInternational(formattedPhoneValue.value.replace(/\D/g, ''), countryCode)
}

const onPhoneInput = (event: Event) => {
  const el = event.target as HTMLInputElement
  const userInput = el.value

  // count digits before cursor — semantic position unaffected by formatting characters
  const cursorPosition = el.selectionStart ?? userInput.length
  const digitsBeforeCursor = userInput.slice(0, cursorPosition).replace(/[^\d+]/g, '').length

  // keep leading + for international numbers, strip everything else non-digit
  const digits = (userInput.startsWith('+') ? '+' : '') + userInput.replace(/\D/g, '')
  const formatted = formatIncompletePhoneNumber(digits, selectedCountryCode.value)

  el.value = formatted // overwrite synchronously to prevent cursor jump
  formattedPhoneValue.value = formatted
  model.value = toInternational(digits, selectedCountryCode.value)

  // restore cursor position
  let digitCount = 0
  let newCursorPosition = formatted.length
  for (let i = 0; i < formatted.length; i++) {
    if (digitCount === digitsBeforeCursor) {
      newCursorPosition = i
      break
    }
    if (/[\d+]/.test(formatted[i])) digitCount++
  }
  el.setSelectionRange(newCursorPosition, newCursorPosition)
}

// on blur — reformat fully once the user is done
const reformatNumber = () => {
  if (model.value)
    formattedPhoneValue.value = toNationalPretty(model.value, selectedCountryCode.value)
}

// if locale changes, change country code only if field was empty
watch(
  () => props.defaultCountryLocale,
  (locale) => {
    if (!model.value) selectedCountryCode.value = locale as CountryCode
  },
  { immediate: true },
)

// value can be changed by user or prefilled from API
watch(
  () => model.value,
  (newValue) => {
    // if new value empty reset input
    if (!newValue) {
      formattedPhoneValue.value = ''
      return
    }

    // update country code if new value has country numbers
    const numberCountry = getPhoneNumberCountry(newValue)
    if (numberCountry) selectedCountryCode.value = numberCountry

    // if new and formatted value is same, do nothing
    const formatted = toNationalPretty(newValue, selectedCountryCode.value)
    if (formatted === formattedPhoneValue.value) return

    // update display number with formatted value
    formattedPhoneValue.value = formatted
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
@use '@/assets/scss/_variables' as vars;

.flag-select {
  width: 76px;
}

.flag {
  height: 13px;
  width: 26px;
}

:deep(.is-invalid .ant-select-selector) {
  border-color: vars.$danger;
}

.ant-select-selection-item .select-label {
  display: none;
}

.group-border {
  border-top: 0.15rem dashed;
}
</style>
